/* Photo Markup — tools and shell behaviour added on top of app.js.
 *
 * app.js owns the canvas engine (Konva stage, history, export). This file owns
 * everything the 2026 rebuild added:
 *
 *   · four new marks — callout, magnifier, measure, sticker — plus the solid
 *     finish of "hide", registered through the PMX pointer hooks app.js exposes
 *   · the context strip: one visible control per meaning, so blur/pixelate/solid
 *     are one tool and arrow/line are one tool
 *   · popovers that escape the strips' own overflow (native top layer where the
 *     browser has it, a fixed-position fallback where it does not)
 *   · the cue line, layer drawer, fullscreen, share sheet and sample image
 *
 * Loaded with `defer` after app.js, so window.PM already exists.
 */
(function () {
    'use strict';

    var I18N = window.PM_I18N || {};
    var $ = function (id) { return document.getElementById(id); };
    var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };

    if (!$('pmApp')) return;

    var PM = null;
    var K = null;

    /* Local state for the tools this file owns. */
    var X = {
        hideStyle: 'blur',          // blur | pixel | solid
        numberStyle: 'circle',
        calloutStyle: 'filled',
        calloutSize: 20,
        magShape: 'circle',
        magZoom: 2,
        magLeader: true,
        measureMode: 'length',      // length | angle
        scale: null,                // { pxPerUnit, unit } once calibrated
        sticker: '📍',
        pendingCalibration: false,
    };

    var STICKERS = [
        '📍', '⭐', '❗', '❓', '✅', '❌', '👍', '👎', '🔥', '💡',
        '⚠️', '🎯', '👀', '💬', '🔒', '🏠', '🚗', '📦', '🛠️', '💰',
        '😀', '😍', '😱', '🤔', '🙌', '❤️', '🚩', '🔗', '📅', '✏️',
    ];

    /* ══════════════════════════════════════════════════════════════════
       Popovers

       The context strip scrolls horizontally, which means it clips on both
       axes — a colour panel positioned inside it would be cut off. Native
       popovers live in the top layer and cannot be clipped; where they are
       missing we position a copy with `fixed` instead. app.js keeps toggling
       `hidden` exactly as before; this only watches that attribute.
       ══════════════════════════════════════════════════════════════════ */

    var NATIVE_POPOVER = typeof HTMLElement !== 'undefined' &&
        typeof HTMLElement.prototype.showPopover === 'function';

    function wirePopover(menuId, btnId) {
        var menu = $(menuId), btn = $(btnId);
        if (!menu || !btn) return;

        if (NATIVE_POPOVER) {
            try { menu.setAttribute('popover', 'manual'); } catch (e) { /* keep fallback */ }
        }

        var reflow = function () { if (!menu.hidden) positionFixed(menu, btn); };
        var place = function () {
            if (NATIVE_POPOVER) {
                try { menu.showPopover(); } catch (e) { /* already open */ }
            }
            positionFixed(menu, btn);
            // A fixed popover does not follow its trigger, and the page under
            // it scrolls. Track both while it is open, drop the listeners when
            // it closes.
            window.addEventListener('scroll', reflow, true);
            window.addEventListener('resize', reflow);
        };
        var unplace = function () {
            window.removeEventListener('scroll', reflow, true);
            window.removeEventListener('resize', reflow);
            if (NATIVE_POPOVER) { try { menu.hidePopover(); } catch (e) {} }
            menu.style.position = '';
            menu.style.insetInlineStart = '';
            menu.style.insetBlockStart = '';
            menu.style.left = ''; menu.style.top = '';
        };

        // `hidden` is still the single source of truth, so app.js's own
        // open/close handlers keep working untouched.
        new MutationObserver(function () {
            if (menu.hidden) unplace(); else place();
        }).observe(menu, { attributes: true, attributeFilter: ['hidden'] });

        if (!menu.hidden) place();
    }

    function positionFixed(el, anchor) {
        var r = anchor.getBoundingClientRect();
        el.style.position = 'fixed';
        el.style.insetInlineStart = 'auto';
        el.style.insetBlockStart = 'auto';
        el.style.insetBlockEnd = 'auto';
        el.style.insetInlineEnd = 'auto';
        // Measure at a known origin first, then decide which side has room.
        el.style.left = '0px'; el.style.top = '0px';
        var w = el.offsetWidth, h = el.offsetHeight;
        var vw = window.innerWidth, vh = window.innerHeight;
        // Right-align to the trigger, then keep the whole panel on screen.
        var leftHalf = (r.left + r.width / 2) < vw / 2;
        var left = leftHalf ? r.left : r.right - w;
        left = Math.min(Math.max(8, left), Math.max(8, vw - w - 8));
        var top = r.bottom + 6;
        if (top + h > vh - 8) top = Math.max(8, r.top - h - 6);
        el.style.left = left + 'px';
        el.style.top = top + 'px';
    }

    /* ══════════════════════════════════════════════════════════════════
       Colour chip
       ══════════════════════════════════════════════════════════════════ */

    function syncColorChip() {
        var dot = $('pmColorDot');
        if (dot && PM) dot.style.setProperty('--pm-color', PM.state.color);
    }

    function wireColorChip() {
        var btn = $('pmColorBtn'), pop = $('pmColorPop');
        if (!btn || !pop) return;

        btn.addEventListener('click', function (e) {
            e.stopPropagation();
            var open = pop.hidden;
            pop.hidden = !open;
            btn.setAttribute('aria-expanded', open ? 'true' : 'false');
            btn.parentElement.classList.toggle('is-open', open);
        });
        pop.addEventListener('click', function (e) { e.stopPropagation(); });
        document.addEventListener('click', function () {
            if (pop.hidden) return;
            pop.hidden = true;
            btn.setAttribute('aria-expanded', 'false');
            btn.parentElement.classList.remove('is-open');
        });
        // Picking a swatch closes the panel: the choice is made.
        $$('.pm-swatch', pop).forEach(function (sw) {
            sw.addEventListener('click', function () {
                setTimeout(function () {
                    pop.hidden = true;
                    btn.setAttribute('aria-expanded', 'false');
                    btn.parentElement.classList.remove('is-open');
                    syncColorChip();
                }, 0);
            });
        });
        var input = $('pmColor');
        if (input) input.addEventListener('input', syncColorChip);
        wirePopover('pmColorPop', 'pmColorBtn');

        // The eyedropper is a way of choosing a colour, so it lives in the
        // colour control rather than competing for a slot in the tool strip.
        var eye = $('pmEyedropper');
        if (eye) eye.addEventListener('click', function (e) {
            e.stopPropagation();
            pop.hidden = true;
            btn.setAttribute('aria-expanded', 'false');
            useNativeEyedropperOrTool();
        });
    }

    function useNativeEyedropperOrTool() {
        // Chrome's EyeDropper can sample the whole screen, which beats sampling
        // our own canvas. Everything else falls back to the canvas tool.
        if (window.EyeDropper) {
            try {
                new window.EyeDropper().open().then(function (res) {
                    if (!res || !res.sRGBHex) return;
                    PM.state.color = res.sRGBHex;
                    var input = $('pmColor');
                    if (input) input.value = res.sRGBHex;
                    PM.api.rememberColor(res.sRGBHex);
                    PM.api.highlightPaletteSwatch(res.sRGBHex);
                    syncColorChip();
                }).catch(function () { /* user cancelled */ });
                return;
            } catch (e) { /* fall through */ }
        }
        PM.api.selectTool('eyedropper');
    }

    /* ══════════════════════════════════════════════════════════════════
       Context strip switches
       ══════════════════════════════════════════════════════════════════ */

    function segGroup(attr, onPick) {
        $$('[' + attr + ']').forEach(function (b) {
            b.addEventListener('click', function () {
                var group = b.parentElement;
                $$('[' + attr + ']', group).forEach(function (x) {
                    x.classList.remove('is-active');
                    if (x.hasAttribute('role')) x.setAttribute('aria-checked', 'false');
                });
                b.classList.add('is-active');
                if (b.hasAttribute('role')) b.setAttribute('aria-checked', 'true');
                onPick(b.getAttribute(attr), b);
            });
        });
    }

    function wireContextStrip() {
        // Hide: one gesture, three finishes. app.js still knows the internal
        // tool names 'blur' and 'pixelate'; the switch drives which one the
        // next drag produces, so the rail carries one entry instead of three.
        segGroup('data-hide-style', function (v) {
            X.hideStyle = v;
            var blurFine = $('pmBlurFine'), pixFine = $('pmPixelFine');
            if (blurFine) blurFine.hidden = (v !== 'blur');
            if (pixFine) pixFine.hidden = (v !== 'pixel');
            if (PM.state.tool === 'blur' || PM.state.tool === 'pixelate') {
                PM.state.tool = (v === 'pixel') ? 'pixelate' : 'blur';
                $('pmCanvasArea').dataset.tool = PM.state.tool;
            }
        });

        segGroup('data-number-style', function (v) { X.numberStyle = v; });
        segGroup('data-callout-style', function (v) { X.calloutStyle = v; });
        segGroup('data-mag-shape', function (v) { X.magShape = v; });
        segGroup('data-measure-mode', function (v) { X.measureMode = v; });

        bindRange('pmCalloutSize', 'pmCalloutSizeVal', function (v) { X.calloutSize = +v; });
        bindRange('pmMagZoom', 'pmMagZoomVal', function (v) { X.magZoom = +v; }, function (v) {
            return (+v).toFixed(1).replace(/\.0$/, '');
        });

        var leader = $('pmMagLeader');
        if (leader) leader.addEventListener('change', function () { X.magLeader = leader.checked; });

        // Arrow "no heads" is the old line tool: same object, different ends.
        // Choosing it while the arrow is active keeps you on the arrow tool.
        $$('[data-arrow-style]').forEach(function (b) {
            b.addEventListener('click', function () {
                PM.state.arrowStyle = b.getAttribute('data-arrow-style');
            });
        });

        var cal = $('pmMeasureCalibrate');
        if (cal) cal.addEventListener('click', startCalibration);

        buildStickers();
    }

    function bindRange(id, valId, apply, fmt) {
        var el = $(id), out = $(valId);
        if (!el) return;
        el.addEventListener('input', function () {
            if (el.value === '' || !isFinite(+el.value)) return;
            apply(el.value);
            if (out) out.textContent = fmt ? fmt(el.value) : el.value;
        });
    }

    function buildStickers() {
        var box = $('pmStickers');
        if (!box) return;
        STICKERS.forEach(function (ch, i) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'pm-sticker' + (i === 0 ? ' is-active' : '');
            b.textContent = ch;
            b.setAttribute('aria-label', ch);
            b.addEventListener('click', function () {
                X.sticker = ch;
                $$('.pm-sticker', box).forEach(function (x) { x.classList.remove('is-active'); });
                b.classList.add('is-active');
            });
            box.appendChild(b);
        });
    }

    /* ══════════════════════════════════════════════════════════════════
       Cue line — the answer to "what does this tool do", where the work is
       ══════════════════════════════════════════════════════════════════ */

    var cueTimer = null;
    function cueText(tool) {
        if (tool === 'select' && PM && PM.konva && PM.konva.drawLayer && !PM.konva.drawLayer.find('.pm-obj').length) {
            return I18N.cueSelectEmpty || (I18N.cues || {}).select || '';
        }
        return (I18N.cues || {})[tool] || '';
    }
    function showCue(tool) {
        var txt = cueText(tool);
        var hint = $('pmCtxHintText');
        if (hint) hint.textContent = txt;
        var el = $('pmCue');
        if (!el) return;
        clearTimeout(cueTimer);
        if (!txt) { el.classList.remove('is-on'); return; }
        el.textContent = txt;
        el.classList.add('is-on');
        cueTimer = setTimeout(function () { el.classList.remove('is-on'); }, 7000);
    }

    /* Context blocks owned by this file. app.js hides the ones it knows about
       and fires pm:tool; we hide ours the same way, from one list. */
    var OWNED = {
        callout: '.pm-ctx-callout',
        magnifier: '.pm-ctx-magnifier',
        measure: '.pm-ctx-measure',
        sticker: '.pm-ctx-sticker',
    };

    function onToolChange(tool) {
        Object.keys(OWNED).forEach(function (name) {
            var el = document.querySelector(OWNED[name]);
            if (el) el.hidden = (tool !== name);
        });
        // Colour, thickness and opacity are the style of the NEXT mark, so they
        // stay put for every drawing tool. They mean nothing while cropping or
        // erasing, and there the strip would otherwise be an empty bar — so a
        // hint takes their place rather than leaving a 44px void.
        var paints = (tool !== 'crop' && tool !== 'eraser');
        toggle('.pm-ctx-color', paints);
        toggle('.pm-ctx-stroke', paints && tool !== 'sticker' && tool !== 'text');
        toggle('.pm-ctx-opacity', paints);

        showCue(tool);
    }

    function toggle(sel, on) {
        var el = document.querySelector(sel);
        if (el) el.hidden = !on;
    }

    /* ══════════════════════════════════════════════════════════════════
       New marks
       ══════════════════════════════════════════════════════════════════ */

    function common() { return { name: 'pm-obj', draggable: false }; }

    /* Callout — a text box with a tail that points at the thing it describes.
       Snagit's signature mark and the one our rail was missing; a bare text
       label makes the reader guess which part of the picture it belongs to. */
    function makeCallout(p, givenText) {
        var color = PM.state.color;
        var size = X.calloutSize;
        var filled = X.calloutStyle === 'filled';
        var pad = Math.round(size * 0.55);
        var text = (givenText != null && String(givenText).trim()) ? givenText : (I18N.calloutPlaceholder || 'Text');

        var label = new K.Text({
            name: 'pm-callout-label',
            text: text, fontSize: size, fontFamily: PM.state.fontFamily,
            fill: filled ? pickReadable(color) : color,
            x: pad, y: pad, lineHeight: 1.25,
        });
        var box = new K.Rect({
            name: 'pm-callout-box',
            x: 0, y: 0, width: 10, height: 10, cornerRadius: Math.min(10, size * 0.45),
            fill: filled ? color : 'rgba(255,255,255,.94)',
            stroke: color, strokeWidth: filled ? 0 : Math.max(2, PM.state.stroke),
        });
        // The tail starts under the box and reaches down-left to the point the
        // user clicked, so the mark reads as "this box is about THAT".
        var tail = new K.Line({
            name: 'pm-callout-tail',
            points: [0, 0, 0, 0, 0, 0], closed: true,
            fill: filled ? color : 'rgba(255,255,255,.94)',
            stroke: color, strokeWidth: filled ? 0 : Math.max(2, PM.state.stroke),
        });
        var g = new K.Group(Object.assign(common(), { opacity: PM.state.opacity, draggable: true }));
        g.add(tail); g.add(box); g.add(label);
        g.setAttr('pmKind', 'callout');
        g.setAttr('pmPad', pad);
        g.setAttr('pmTailLen', Math.max(26, size * 1.4));
        layoutCallout(g);
        g.x(Math.max(0, p.x - box.width() * 0.10));
        g.y(Math.max(0, p.y - box.height() - g.getAttr('pmTailLen')));
        return g;
    }

    /* Size the box and the tail around whatever the label says now. Called on
       creation and again after in-place editing. */
    function layoutCallout(g) {
        var label = g.findOne('.pm-callout-label'), box = g.findOne('.pm-callout-box'), tail = g.findOne('.pm-callout-tail');
        if (!label || !box || !tail) return;
        var pad = g.getAttr('pmPad') || 10, tl = g.getAttr('pmTailLen') || 26;
        var w = label.width() + pad * 2, h = label.height() + pad * 2;
        box.width(w); box.height(h);
        tail.points([w * 0.28, h, w * 0.10, h + tl, w * 0.46, h]);
    }

    function pickReadable(hex) {
        var c = String(hex || '#000').replace('#', '');
        if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
        var r = parseInt(c.slice(0, 2), 16), g = parseInt(c.slice(2, 4), 16), b = parseInt(c.slice(4, 6), 16);
        // Rec. 709 luma — the same rule the export path uses for text outlines.
        return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 150 ? '#111111' : '#ffffff';
    }

    /* Magnifier — a lens that enlarges one region and leaves the rest legible.
       Competitors call it Zoom (Annotely) or Magnify (Snagit). Ours samples
       the SOURCE bitmap, so the inset stays sharp at export scale instead of
       enlarging already-rendered pixels. */
    function makeMagnifier(bbox) {
        var src = PM.konva.imageNode.image();
        var zoom = X.magZoom;
        var cx = bbox.x + bbox.w / 2, cy = bbox.y + bbox.h / 2;

        // A round lens must sample a SQUARE, or the circle clips away the sides
        // of a wide sample and the detail sits off-centre inside its own lens.
        if (X.magShape === 'circle') {
            var side = Math.max(bbox.w, bbox.h);
            bbox = {
                x: Math.max(0, Math.min(PM.state.imgW - side, cx - side / 2)),
                y: Math.max(0, Math.min(PM.state.imgH - side, cy - side / 2)),
                w: side, h: side,
            };
            cx = bbox.x + side / 2; cy = bbox.y + side / 2;
        }

        var outW = bbox.w * zoom, outH = bbox.h * zoom;

        // Sample above display size so a 2x/3x export stays sharp instead of
        // upscaling an already-rasterised inset.
        var SS = 2;
        var c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(outW * SS));
        c.height = Math.max(1, Math.round(outH * SS));
        var ctx = c.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(src, bbox.x, bbox.y, bbox.w, bbox.h, 0, 0, c.width, c.height);

        var g = new K.Group(Object.assign(common(), { draggable: true, opacity: PM.state.opacity }));
        g.setAttr('pmKind', 'magnifier');

        // The lens sits above and right of the sampled area by default, so it
        // does not cover the thing it is showing.
        var lx = Math.min(PM.state.imgW - outW, cx + bbox.w * 0.6);
        var ly = Math.max(0, cy - outH - bbox.h * 0.4);
        var round = X.magShape === 'circle';
        var r = Math.min(outW, outH) / 2;

        if (X.magLeader) {
            g.add(new K.Line({
                points: [cx, cy, lx + outW / 2, ly + outH / 2],
                stroke: PM.state.color, strokeWidth: Math.max(1.5, PM.state.stroke * 0.6),
                dash: [6, 4], listening: false,
            }));
            g.add(new K.Rect({
                x: bbox.x, y: bbox.y, width: bbox.w, height: bbox.h,
                stroke: PM.state.color, strokeWidth: Math.max(1.5, PM.state.stroke * 0.6),
                dash: [4, 3], listening: false,
            }));
        }

        var img = new K.Image({
            image: c, x: lx, y: ly, width: outW, height: outH,
        });
        if (round) {
            img.clipFunc = null;
            var grp = new K.Group({ x: lx, y: ly, clipFunc: function (cctx) {
                cctx.arc(outW / 2, outH / 2, r, 0, Math.PI * 2, false);
            } });
            img.x(0); img.y(0);
            grp.add(img);
            g.add(grp);
            g.add(new K.Circle({
                x: lx + outW / 2, y: ly + outH / 2, radius: r,
                stroke: PM.state.color, strokeWidth: Math.max(2, PM.state.stroke),
            }));
        } else {
            g.add(img);
            g.add(new K.Rect({
                x: lx, y: ly, width: outW, height: outH, cornerRadius: 6,
                stroke: PM.state.color, strokeWidth: Math.max(2, PM.state.stroke),
            }));
        }
        return g;
    }

    /* Measure — a dimension line that reports pixels, or real units once the
       picture has been calibrated against something of known size. No browser
       annotator we found offers it, and it is the whole job for a repair
       estimate, a room photo or a damage report. */
    function makeMeasure(a, b) {
        var dx = b.x - a.x, dy = b.y - a.y;
        var px = Math.hypot(dx, dy);
        if (px < 6) return null;

        var color = PM.state.color;
        var sw = Math.max(1.5, PM.state.stroke * 0.8);
        var g = new K.Group(Object.assign(common(), { draggable: true, opacity: PM.state.opacity }));
        g.setAttr('pmKind', 'measure');

        g.add(new K.Arrow({
            points: [a.x, a.y, b.x, b.y],
            stroke: color, fill: color, strokeWidth: sw,
            pointerLength: Math.max(8, sw * 3), pointerWidth: Math.max(8, sw * 3),
            pointerAtBeginning: true,
        }));

        // End ticks, perpendicular to the run — the convention on any drawing.
        var ang = Math.atan2(dy, dx) + Math.PI / 2;
        var tick = Math.max(7, sw * 4);
        [a, b].forEach(function (pt) {
            g.add(new K.Line({
                points: [
                    pt.x - Math.cos(ang) * tick, pt.y - Math.sin(ang) * tick,
                    pt.x + Math.cos(ang) * tick, pt.y + Math.sin(ang) * tick,
                ],
                stroke: color, strokeWidth: sw,
            }));
        });

        var label = formatMeasure(px);
        var fs = Math.max(13, PM.state.stroke * 3.4);
        var txt = new K.Text({ text: label, fontSize: fs, fontFamily: PM.state.fontFamily, fill: pickReadable(color) });
        var padX = fs * 0.45, padY = fs * 0.22;
        var mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
        var bw = txt.width() + padX * 2, bh = txt.height() + padY * 2;
        var plate = new K.Rect({
            x: mx - bw / 2, y: my - bh / 2 - tick - bh * 0.35,
            width: bw, height: bh, cornerRadius: 5, fill: color,
        });
        txt.x(plate.x() + padX); txt.y(plate.y() + padY);
        g.add(plate); g.add(txt);
        g.setAttr('pmMeasurePx', px);
        return g;
    }

    function formatMeasure(px) {
        if (X.measureMode === 'angle') return '';
        if (X.scale && X.scale.pxPerUnit > 0) {
            var v = px / X.scale.pxPerUnit;
            var s = v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2);
            return s + ' ' + X.scale.unit;
        }
        return Math.round(px) + ' px';
    }

    function makeAngle(pts) {
        // Three points: vertex is the middle one.
        var color = PM.state.color;
        var sw = Math.max(1.5, PM.state.stroke * 0.8);
        var v = pts[1], p1 = pts[0], p2 = pts[2];
        var a1 = Math.atan2(p1.y - v.y, p1.x - v.x);
        var a2 = Math.atan2(p2.y - v.y, p2.x - v.x);
        var deg = Math.abs((a2 - a1) * 180 / Math.PI);
        if (deg > 180) deg = 360 - deg;

        var g = new K.Group(Object.assign(common(), { draggable: true, opacity: PM.state.opacity }));
        g.setAttr('pmKind', 'angle');
        g.add(new K.Line({ points: [p1.x, p1.y, v.x, v.y, p2.x, p2.y], stroke: color, strokeWidth: sw, lineJoin: 'round' }));
        var r = Math.min(46, Math.hypot(p1.x - v.x, p1.y - v.y) * 0.45);
        g.add(new K.Arc({
            x: v.x, y: v.y, innerRadius: r, outerRadius: r,
            angle: deg, rotation: Math.min(a1, a2) * 180 / Math.PI,
            stroke: color, strokeWidth: sw,
        }));
        var fs = Math.max(13, PM.state.stroke * 3.4);
        var txt = new K.Text({ text: deg.toFixed(1) + '°', fontSize: fs, fontFamily: PM.state.fontFamily, fill: pickReadable(color) });
        var padX = fs * 0.45, padY = fs * 0.22;
        var plate = new K.Rect({ x: v.x + r * 0.7, y: v.y - r * 0.7, width: txt.width() + padX * 2, height: txt.height() + padY * 2, cornerRadius: 5, fill: color });
        txt.x(plate.x() + padX); txt.y(plate.y() + padY);
        g.add(plate); g.add(txt);
        return g;
    }

    /* Calibration: drag across something whose real size you know, type that
       size, and every measurement on the picture switches to those units. */
    function startCalibration() {
        X.pendingCalibration = true;
        PM.api.selectTool('measure');
        PM.api.toast(I18N.measurePrompt || 'Drag across something of known size');
    }

    function finishCalibration(px) {
        X.pendingCalibration = false;
        var answer = window.prompt(I18N.measureUnitHint || 'Real length, e.g. 120 cm', '');
        if (!answer) return false;
        var m = String(answer).trim().match(/^([\d.,]+)\s*([^\d\s]*)$/);
        if (!m) return false;
        var val = parseFloat(m[1].replace(',', '.'));
        if (!(val > 0)) return false;
        var unit = (m[2] || 'cm').trim();
        X.scale = { pxPerUnit: px / val, unit: unit };
        var lab = $('pmScaleLabel');
        if (lab) lab.textContent = (I18N.measureScaleSet || '1 {u} = {n} px')
            .replace('{u}', unit).replace('{n}', X.scale.pxPerUnit.toFixed(1));
        PM.api.toast((I18N.measureScaleSet || 'Scale set') + '');
        PM.api.trackFeature('measure_calibrated', { unit: unit });
        return true;
    }

    /* Solid block — the third finish of "hide". Unlike blur and pixelate it
       carries no trace of what was under it, which is the point when the thing
       being covered is a document number rather than a face. */
    function makeSolid(bbox) {
        return new K.Rect(Object.assign(common(), {
            x: bbox.x, y: bbox.y, width: bbox.w, height: bbox.h,
            fill: PM.state.color, draggable: true,
        }));
    }

    /* ══════════════════════════════════════════════════════════════════
       PMX — the pointer hooks app.js calls
       ══════════════════════════════════════════════════════════════════ */

    window.PMX = {
        onDown: function (tool, e) {
            if (!PM || !PM.konva || !PM.konva.stage) return false;
            // A stage means Konva is here — the shell was wired without it.
            if (!K) K = window.Konva || null;
            if (!K) return false;
            var p = PM.api.stagePointer();

            if (tool === 'sticker') {
                var s = new K.Text(Object.assign(common(), {
                    text: X.sticker,
                    fontSize: Math.max(28, PM.state.stroke * 9),
                    x: p.x, y: p.y, draggable: true, opacity: PM.state.opacity,
                }));
                s.x(p.x - s.width() / 2); s.y(p.y - s.height() / 2);
                s.setAttr('pmKind', 'sticker');
                PM.konva.drawLayer.add(s);
                PM.konva.drawLayer.batchDraw();
                PM.api.pushHistory();
                PM.api.refreshLayerPanel();
                PM.api.trackFeature('sticker_add', { ch: X.sticker });
                PM.ui.drawing = null;
                return true;
            }

            if (tool === 'callout') {
                var g = makeCallout(p);
                PM.konva.drawLayer.add(g);
                PM.konva.drawLayer.batchDraw();
                // Edit the label in place, like the Text tool. An empty commit
                // removes the whole callout, not just its text.
                var label = g.findOne('.pm-callout-label');
                if (PM.api.enterTextEdit && label) {
                    PM.api.enterTextEdit(label, function (v) {
                        if (!v) { g.destroy(); return; }
                        layoutCallout(g);
                    });
                } else {
                    PM.api.pushHistory();
                }
                PM.api.refreshLayerPanel();
                PM.api.trackFeature('callout_add', { style: X.calloutStyle });
                PM.ui.drawing = null;
                return true;
            }

            if (tool === 'magnifier' || tool === 'measure') {
                var preview = (tool === 'magnifier')
                    ? new K.Rect({
                        x: p.x, y: p.y, width: 0, height: 0,
                        stroke: PM.state.color, strokeWidth: 2, dash: [6, 4],
                        fill: 'rgba(255,255,255,.08)', listening: false,
                    })
                    : new K.Line({
                        points: [p.x, p.y, p.x, p.y],
                        stroke: PM.state.color, strokeWidth: Math.max(1.5, PM.state.stroke * 0.8),
                        dash: [6, 4], listening: false,
                    });
                PM.konva.overlayLayer.add(preview);
                PM.ui.drawing = { type: tool, start: p, points: [p], node: preview, pmx: true };
                PM.konva.overlayLayer.batchDraw();
                return true;
            }

            // "hide" with the solid finish: same drag, no filter step.
            if ((tool === 'blur' || tool === 'pixelate') && X.hideStyle === 'solid') {
                var r = new K.Rect({
                    x: p.x, y: p.y, width: 0, height: 0,
                    fill: PM.state.color, opacity: 0.85, listening: false,
                });
                PM.konva.overlayLayer.add(r);
                PM.ui.drawing = { type: 'solid', start: p, points: [p], node: r, pmx: true };
                PM.konva.overlayLayer.batchDraw();
                return true;
            }

            return false;
        },

        onMove: function (d, e) {
            if (!d || !d.pmx) return false;
            var p = PM.api.stagePointer();
            var shift = e && e.evt && e.evt.shiftKey;

            if (d.type === 'measure') {
                var ex = p.x, ey = p.y;
                if (shift) {
                    var ddx = p.x - d.start.x, ddy = p.y - d.start.y;
                    var ang = Math.round(Math.atan2(ddy, ddx) / (Math.PI / 4)) * (Math.PI / 4);
                    var rr = Math.hypot(ddx, ddy);
                    ex = d.start.x + Math.cos(ang) * rr;
                    ey = d.start.y + Math.sin(ang) * rr;
                }
                d.node.points([d.start.x, d.start.y, ex, ey]);
                d.end = { x: ex, y: ey };
            } else {
                var w = p.x - d.start.x, h = p.y - d.start.y;
                if (shift && d.type === 'magnifier') {
                    var sq = Math.max(Math.abs(w), Math.abs(h));
                    w = (w < 0 ? -1 : 1) * sq; h = (h < 0 ? -1 : 1) * sq;
                }
                if (w < 0) { d.node.x(d.start.x + w); d.node.width(-w); } else { d.node.x(d.start.x); d.node.width(w); }
                if (h < 0) { d.node.y(d.start.y + h); d.node.height(-h); } else { d.node.y(d.start.y); d.node.height(h); }
            }
            PM.konva.overlayLayer.batchDraw();
            return true;
        },

        onUp: function (d, shouldPush) {
            if (!d || !d.pmx) return false;
            var node = d.node;

            if (d.type === 'measure') {
                var a = d.start, b = d.end || d.start;
                node.destroy();
                PM.konva.overlayLayer.batchDraw();
                var px = Math.hypot(b.x - a.x, b.y - a.y);
                if (px < 6) return true;

                if (X.pendingCalibration) {
                    if (finishCalibration(px)) PM.api.toast(labelForScale());
                    return true;
                }
                var g = (X.measureMode === 'angle')
                    ? makeAngle([a, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, b])
                    : makeMeasure(a, b);
                if (g) {
                    PM.konva.drawLayer.add(g);
                    PM.konva.drawLayer.batchDraw();
                    if (shouldPush) PM.api.pushHistory();
                    PM.api.refreshLayerPanel();
                    PM.api.trackFeature('measure_add', { mode: X.measureMode, calibrated: X.scale ? 1 : 0 });
                }
                return true;
            }

            var bbox = { x: node.x(), y: node.y(), w: node.width(), h: node.height() };
            node.destroy();
            PM.konva.overlayLayer.batchDraw();
            if (bbox.w < 8 || bbox.h < 8) return true;

            var made = (d.type === 'magnifier') ? makeMagnifier(bbox) : makeSolid(bbox);
            if (made) {
                PM.konva.drawLayer.add(made);
                PM.konva.drawLayer.batchDraw();
                if (shouldPush) PM.api.pushHistory();
                PM.api.refreshLayerPanel();
                PM.api.trackFeature(d.type === 'magnifier' ? 'magnifier_add' : 'hide_solid_add', {});
            }
            return true;
        },
    };

    function labelForScale() {
        if (!X.scale) return '';
        return (I18N.measureScaleSet || 'Scale set');
    }

    /* ══════════════════════════════════════════════════════════════════
       Shell: layers drawer, fullscreen, share, sample
       ══════════════════════════════════════════════════════════════════ */

    function wireLayersDrawer() {
        var btn = $('pmLayersBtn'), pane = $('pmRightPanel'), close = $('pmLayersClose');
        if (!btn || !pane) return;
        var set = function (open) {
            pane.hidden = !open;
            btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        };
        btn.addEventListener('click', function () { set(pane.hidden); });
        if (close) close.addEventListener('click', function () { set(false); });
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && !pane.hidden) set(false);
        });

        // The badge answers "is there anything in there" without opening it.
        var list = $('pmLayers'), count = $('pmLayerCount');
        if (!list || !count) return;
        var update = function () {
            var n = list.querySelectorAll('.pm-layer:not(.pm-layer-orig)').length;
            count.textContent = String(n);
            count.hidden = n === 0;
        };
        new MutationObserver(update).observe(list, { childList: true, subtree: true });
        update();
    }

    function wireFullscreen() {
        var btn = $('pmFullscreen'), shell = $('pmWorkspace');
        if (!btn || !shell) return;
        if (!shell.requestFullscreen && !shell.webkitRequestFullscreen) { btn.hidden = true; return; }
        btn.addEventListener('click', function () {
            var fs = document.fullscreenElement || document.webkitFullscreenElement;
            if (fs) {
                (document.exitFullscreen || document.webkitExitFullscreen).call(document);
            } else {
                (shell.requestFullscreen || shell.webkitRequestFullscreen).call(shell);
                PM.api.trackFeature('fullscreen_enter', {});
            }
        });
        // The stage is sized from its container, so it must be re-fit after the
        // box changes; a resize event alone does not always arrive in Safari.
        ['fullscreenchange', 'webkitfullscreenchange'].forEach(function (ev) {
            document.addEventListener(ev, function () {
                setTimeout(function () { window.dispatchEvent(new Event('resize')); }, 60);
            });
        });
    }

    /* Share — on a phone this is the whole point: mark up a photo and send it
       straight to the chat it was going to. Only mounted where the browser
       will actually accept a file. */
    function wireShare() {
        var btn = $('pmShare');
        if (!btn) return;
        if (!(navigator.canShare && navigator.share)) return;
        try {
            var probe = new File([new Blob([new Uint8Array([0])], { type: 'image/png' })], 'p.png', { type: 'image/png' });
            if (!navigator.canShare({ files: [probe] })) return;
        } catch (e) { return; }
        btn.hidden = false;

        btn.addEventListener('click', function () {
            var stage = PM.konva && PM.konva.stage;
            if (!stage) return;
            var lim = window.__toolLimit;
            var run = (lim && lim.begin) ? lim.begin('photo-markup', [PM.state.limitKey || 'session']) : { commit: function () {}, abort: function () {} };
            if (!run) return;
            try {
                var url = stage.toDataURL({ pixelRatio: PM.state.retina || 1, mimeType: 'image/png' });
                fetch(url).then(function (r) { return r.blob(); }).then(function (blob) {
                    var f = new File([blob], 'markup.png', { type: 'image/png' });
                    return navigator.share({ files: [f], title: I18N.shareTitle || 'Markup' });
                }).then(function () {
                    run.commit();
                    PM.api.trackFeature('share_ok', {});
                }).catch(function (err) {
                    run.abort();
                    // AbortError is the person closing the sheet, not a failure.
                    if (err && err.name === 'AbortError') return;
                    PM.api.toast(I18N.shareFail || 'Share failed', 'error');
                });
            } catch (err) { run.abort(); }
        });
    }

    /* A sample image so the tool explains itself before you own a file to try
       it on. Drawn locally — no asset, no request, and it is a screenshot-like
       scene rather than a photo, because that is what most people bring. */
    function wireSample() {
        var btn = $('pmTrySample');
        if (!btn) return;
        btn.addEventListener('click', function () {
            var c = document.createElement('canvas');
            c.width = 1280; c.height = 800;
            var x = c.getContext('2d');
            // A settings screen with real content: a dark title bar, a form with
            // labels, a primary button, a card — so every mark has a target.
            var g = x.createLinearGradient(0, 0, 1280, 800);
            g.addColorStop(0, '#e8edf7'); g.addColorStop(1, '#dfe4f2');
            x.fillStyle = g; x.fillRect(0, 0, 1280, 800);
            x.fillStyle = '#ffffff'; roundRect(x, 80, 70, 1120, 660, 18); x.fill();
            x.fillStyle = '#1e293b'; roundRect(x, 80, 70, 1120, 64, 18); x.fill();
            x.fillStyle = '#1e293b'; x.fillRect(80, 110, 1120, 24);
            ['#ef4444', '#f59e0b', '#22c55e'].forEach(function (col, i) {
                x.fillStyle = col; x.beginPath(); x.arc(116 + i * 26, 102, 7, 0, 6.3); x.fill();
            });
            x.fillStyle = '#e2e8f0'; x.font = '600 18px system-ui, sans-serif';
            x.fillText(I18N.sampleTitle || 'Settings', 210, 109);

            x.fillStyle = '#0f172a'; x.font = '600 28px system-ui, sans-serif';
            x.fillText(I18N.sampleName || 'Sample', 130, 200);
            x.fillStyle = '#475569'; x.font = '18px system-ui, sans-serif';
            x.fillText('name@example.org', 130, 246);
            x.fillText('+7 900 000-00-00', 130, 282);

            x.fillStyle = '#64748b'; x.font = '600 14px system-ui, sans-serif';
            x.fillText('LOGIN', 130, 340); x.fillText('PASSWORD', 130, 430);
            x.strokeStyle = '#cbd5e1'; x.lineWidth = 2;
            roundRect(x, 130, 352, 520, 54, 10); x.stroke();
            roundRect(x, 130, 442, 520, 54, 10); x.stroke();
            x.fillStyle = '#94a3b8'; x.font = '17px system-ui, sans-serif';
            x.fillText('ivan_petrov', 150, 386); x.fillText('••••••••', 150, 476);

            x.fillStyle = '#2563eb'; roundRect(x, 130, 540, 230, 56, 10); x.fill();
            x.fillStyle = '#fff'; x.font = '600 19px system-ui, sans-serif';
            x.fillText(I18N.sampleButton || 'Save', 168, 576);

            x.fillStyle = '#f1f5f9'; roundRect(x, 720, 330, 430, 270, 12); x.fill();
            x.fillStyle = '#cbd5e1'; roundRect(x, 750, 360, 370, 150, 8); x.fill();
            x.fillStyle = '#94a3b8'; x.font = '15px system-ui, sans-serif';
            x.fillText('1920 × 1080', 890, 440);
            x.fillStyle = '#334155'; x.font = '600 15px system-ui, sans-serif';
            x.fillText('16:9', 750, 555);

            X.sampleArmed = true;
            c.toBlob(function (blob) {
                if (!blob) { X.sampleArmed = false; return; }
                var f = new File([blob], SAMPLE_NAME, { type: 'image/png' });
                var input = $('pmUpload') && $('pmUpload').querySelector('input[type=file]');
                var dt = new DataTransfer();
                dt.items.add(f);
                if (input) {
                    input.files = dt.files;
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                } else {
                    X.sampleArmed = false;
                }
                PM.api.trackFeature('sample_open', {});
            }, 'image/png');
        });

        // Once the engine has placed the picture, lay the marks on it. The marks
        // are ordinary editable objects: click one and it is selected, which
        // teaches Select/move/delete without a word. The signal is the STAGE
        // (app.js, after buildStage), not the editor appearing: with a late
        // Konva the editor is shown long before there is a layer to draw on.
        window.addEventListener('pm:stage-ready', function (e) {
            if (!X.sampleArmed) return;
            X.sampleArmed = false;
            // Another picture opened while the sample waited wins — without the
            // sample's marks on it.
            var name = e && e.detail && e.detail.name;
            if (name && name !== SAMPLE_NAME) return;
            demoMarks();
        });
    }
    var SAMPLE_NAME = 'sample.png';

    function demoMarks() {
        if (!K) K = window.Konva || null;
        if (!K || !PM || !PM.konva || !PM.konva.drawLayer) return;
        var layer = PM.konva.drawLayer;
        var red = '#ff3b30', blue = '#007aff', green = '#34c759';

        // 1. Arrow to the primary button.
        layer.add(new K.Arrow({
            name: 'pm-obj', draggable: true,
            points: [560, 660, 380, 585],
            stroke: red, fill: red, strokeWidth: 6, pointerLength: 18, pointerWidth: 18, hitStrokeWidth: 20,
        }));

        // 2. Callout that says what to do.
        var savedColor = PM.state.color, savedSize = X.calloutSize, savedFont = PM.state.fontSize;
        PM.state.color = red; X.calloutSize = 22;
        var callout = makeCallout({ x: 470, y: 500 }, I18N.sampleCallout || 'Click here');
        if (callout) { callout.x(560); callout.y(600); layer.add(callout); }

        // 3. Numbered steps on the two fields.
        PM.state.color = blue; PM.state.stroke = 4;
        PM.api.addNumberedStep({ x: 105, y: 379 });
        PM.api.addNumberedStep({ x: 105, y: 469 });

        // 4. The phone number is pixelated — the hide tool at work.
        PM.api.applyAreaFilter('pixelate', { x: 124, y: 262, w: 200, h: 30 });

        // 5. A magnifier on the small resolution label.
        PM.state.color = green; PM.state.stroke = 3;
        X.magShape = 'circle'; X.magZoom = 2.5;
        var mag = makeMagnifier({ x: 880, y: 425, w: 110, h: 24 });
        if (mag) layer.add(mag);

        // 6. A measured span across the card, in the tool's own units.
        PM.state.color = '#af52de'; PM.state.stroke = 3;
        var m = makeMeasure({ x: 750, y: 640 }, { x: 1120, y: 640 });
        if (m) layer.add(m);

        PM.state.color = savedColor; X.calloutSize = savedSize; PM.state.fontSize = savedFont;
        layer.batchDraw();
        PM.api.pushHistory();
        PM.api.refreshLayerPanel();
        PM.api.selectTool('select');
        PM.api.trackFeature('sample_marks', { n: layer.find('.pm-obj').length });
    }

    function roundRect(ctx, x, y, w, h, r) {
        ctx.beginPath();
        if (ctx.roundRect) { ctx.roundRect(x, y, w, h, r); return; }
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
    }

    /* ══════════════════════════════════════════════════════════════════
       Boot
       ══════════════════════════════════════════════════════════════════ */

    /* The editor sits below the intro and the how-to, which is the site-wide
       block order and does not change for one tool. The cue offers the trip
       instead of taking it: `block: 'nearest'` moves by the minimum amount and
       does nothing at all once the editor is already in view. */
    function wireJumpCue() {
        var cue = $('pmJump'), app = $('pmApp');
        if (!cue || !app || !('IntersectionObserver' in window)) return;
        var dismissed = false;

        cue.addEventListener('click', function () {
            dismissed = true;
            cue.hidden = true;
            var target = $('pmUploadScreen');
            if (!target || target.hidden) target = $('pmWorkspace');
            (target || app).scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });

        new IntersectionObserver(function (entries) {
            if (dismissed) return;
            cue.hidden = entries[0].isIntersecting;
        }, { rootMargin: '-10% 0px -10% 0px' }).observe(app);
    }

    function boot() {
        PM = window.PM;
        if (!PM || !PM.api || !PM.api.stagePointer) return false;
        // The shell does not need Konva — only the marks do, and they read it when
        // they are made (PMX.onDown, demoMarks). Until 04.11.2026 boot waited for
        // Konva by polling 80 × 60 ms and then gave up for good: a late Konva left
        // the colour chip, the context strip, the drawer, fullscreen, share, both
        // menus and the sample unwired for the life of the page.
        K = window.Konva || null;

        wireColorChip();
        wireContextStrip();
        wireLayersDrawer();
        wireFullscreen();
        wireShare();
        wireSample();
        wireJumpCue();
        wirePopover('pmMoreMenu', 'pmMoreBtn');
        wirePopover('pmDownloadMenu', 'pmDownloadBtn');
        syncColorChip();

        document.addEventListener('pm:tool', function (e) { onToolChange(e.detail.tool); });
        onToolChange(PM.state.tool || 'select');
        return true;
    }

    /* Called by app.js right after a letter shortcut has chosen the tool. The
       merged tools keep their old keys: L is the arrow with no heads, X is Hide
       with the pixel finish, F opens Hide and starts the face sweep. */
    PMX.onShortcut = function (letter) {
        if (letter === 'l') return press('[data-arrow-style="none"]');
        if (letter === 'a') return press('[data-arrow-style="single"]');
        if (letter === 'x') return press('[data-hide-style="pixel"]');
        if (letter === 'b') return press('[data-hide-style="blur"]');
        if (letter === 'f') {
            press('[data-hide-style="blur"]');
            var go = $('pmDetectFaces');
            if (go) go.click();
        }
    };

    function press(sel) {
        var b = document.querySelector(sel);
        if (b) b.click();
    }

    // Both files are deferred and run in order, so window.PM is already here;
    // DOMContentLoaded is the second chance should it not be.
    if (!boot()) document.addEventListener('DOMContentLoaded', boot, { once: true });
})();
